import React, { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, IconButton, Typography, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachmentIcon from '@mui/icons-material/Attachment';
import { ButtonUI, CheckboxUI } from '../../Interface';
import CheckListDialog from './CheckListDialog';
import checkListService from '../../../service/checkList.service';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import CheckListMandatoryDialog from './CheckListMandatoryDialog';

const CheckListItemContent = ({ item, disabled, handleCheck, handleOpenMandatoryDialog }) => {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={0.5}>
      <CheckboxUI
        sx={{ m: 0, p: 0 }}
        disabled={disabled}
        checked={item.checked}
        onChange={(e) => handleCheck(item, e.target.checked)}
      />

      <Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body1">{item.name}</Typography>

          {item.itemAttachment.length > 0 && (
            <IconButton disabled={disabled} sx={{ p: 0.25 }} onClick={() => handleOpenMandatoryDialog(item)}>
              <AttachmentIcon />
            </IconButton>
          )}
        </Stack>

        {item.description && (
          <Typography variant="body2" color="text.secondary">
            {item.description}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const CheckLists = ({ checkLists, submenuId, user, lineId, setRefetchTable, disabled, selectedValues, values }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [mandatoryDialogData, setMandatoryDialogData] = useState({ open: false, data: {} });

  useEffect(() => {
    setRows(checkLists);
  }, [checkLists]);

  useEffect(() => {
    getAllTemplate();
  }, []);

  const getAllTemplate = () => {
    checkListService.getAllTemplate().then((data) => {
      setTemplates(data);
    });
  };

  const patchItemById = (itemId, patch) => {
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        checkListItem: row.checkListItem?.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) || [],
      }))
    );
  };

  const handleCreateCheckList = (values) => {
    const insertObject = {
      ...values,
      createdBy: user.userId,
    };

    checkListService
      .createCheckList(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setIsDialogOpen(false);
        setRefetchTable(true);
        setRows((prev) => [...prev, data]);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleUpdateCheckList = (values) => {
    const updateObject = {
      ...values,
      updatedBy: user.userId,
    };

    checkListService
      .updateCheckList(updateObject, selectedChecklist)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setIsDialogOpen(false);
        setRefetchTable(true);

        setRows((prevRows) => prevRows.map((row) => (row.id === data.id ? { ...row, ...data } : row)));
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleDeleteCheckList = () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    checkListService.deleteCheckList(deleteData, selectedChecklist).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setRows(rows.filter((row) => row.id !== selectedChecklist));
    });
  };

  const changeCheckedStatus = async (item, nextChecked, extra) => {
    const payload = {
      checked: nextChecked,
      description: extra?.description,
      existingFiles: extra?.itemAttachment
        ? extra?.itemAttachment?.map((a) => a.name)
        : item?.itemAttachment?.map((a) => a.name),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));

    if (extra?.itemAttachment) {
      for (const a of extra?.itemAttachment) {
        const file = a instanceof File ? a : a?.file;
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    const resp = await checkListService.updateStatus(item.id, formData);
    addNotification({ content: 'Sikeres módosítás!', type: NotificationType.SUCCESS });

    patchItemById(item.id, resp);
    setRefetchTable(true);
  };

  const templateOptions = values.projectTypeId
    ? templates.filter((template) => template.projectTypeId === values.projectTypeId)
    : templates;

  const handleOpenMandatoryDialog = (data) => {
    setMandatoryDialogData({ open: true, data });
  };

  const handleCheck = (item, checked) => {
    const isRequired = item.attachRequired || item.descriptionRequired;

    if (checked && isRequired) {
      handleOpenMandatoryDialog(item);
    } else {
      changeCheckedStatus(item, checked);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <ButtonUI
          text="Checklista létrehozás / módosítása"
          className="bg-success"
          disabled={!selectedValues.projectId || disabled}
          size="sm"
          onClick={() => setIsDialogOpen(true)}
        />
      </div>
      <div className="max-h-[300px] overflow-auto custom-scrollbar px-1">
        {rows.map((row) => {
          const checkedCount = row.checkListItem ? row.checkListItem.filter((item) => item.checked).length : 0;
          return (
            <Accordion key={row.id} className="mb-2">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {row.name}{' '}
                <span className="ml-2">({`${row.checkListItem ? row.checkListItem.length : 0}/${checkedCount}`})</span>
              </AccordionSummary>
              {row.checkListItem &&
                row.checkListItem.map((item) => (
                  <AccordionDetails key={item.id}>
                    <CheckListItemContent
                      item={item}
                      disabled={disabled}
                      handleCheck={handleCheck}
                      handleOpenMandatoryDialog={handleOpenMandatoryDialog}
                    />
                  </AccordionDetails>
                ))}
            </Accordion>
          );
        })}
      </div>
      <CheckListDialog
        rows={rows}
        open={isDialogOpen}
        handleCreate={handleCreateCheckList}
        handleUpdate={handleUpdateCheckList}
        handleDelete={handleDeleteCheckList}
        lineId={lineId}
        user={user}
        submenuId={submenuId}
        handleClose={() => {
          setIsDialogOpen(false);
        }}
        checkList={{ selectedChecklist, setSelectedChecklist }}
        templates={templateOptions}
      />

      <CheckListMandatoryDialog
        open={mandatoryDialogData.open}
        handleClose={() =>
          setMandatoryDialogData({
            open: false,
          })
        }
        setRows={setRows}
        data={mandatoryDialogData.data}
        changeCheckedStatus={changeCheckedStatus}
      />
    </div>
  );
};

export default CheckLists;
